import apiClient from './client';

export interface CorePolicyDto {
  id: string;
  policyNumber: string;
  quoteNumber: string;
  digitalCoverNoteNumber: string;
  productCode: string;
  productName: string;
  lineOfBusiness: string | number;
  policyholderName: string;
  policyholderEmail: string;
  policyholderPhone: string;
  channel: string;
  inceptionDate: string;
  expiryDate: string;
  status: string | number;
  totalSumInsured: number;
  netUnderwritingPremium: number;
  grossWrittenPremium: number;
  commissionAmount: number;
  versionNumber: number;
  riskItems?: Array<{
    registrationNumber: string;
    make: string;
    model: string;
    yearOfManufacture: number;
    sumInsured: number;
  }>;
  debitNotes?: Array<{
    id: string;
    documentNumber: string;
    amount: number;
    status: string | number;
  }>;
}

export interface QuoteCalculationRequest {
  productCode: string;
  coverType: number;
  usageCategory: number;
  sumInsured: number;
  make: string;
  model: string;
  yearOfManufacture: number;
  hasTrackingDevice: boolean;
  driverAge: number;
  driverExperienceYears: number;
  noClaimDiscountYears: number;
  includeExcessBuyback?: boolean;
  includeWindscreenExtension?: boolean;
  windscreenLimit?: number;
}

export interface QuoteCalculationResult {
  productCode: string;
  sumInsured: number;
  basePremium: number;
  totalDiscounts: number;
  totalAddOns: number;
  netUnderwritingPremium: number;
  trainingLevy: number;
  stampDuty: number;
  valueAddedTax: number;
  grossWrittenPremium: number;
  underwritingDecision: number;
  referralReasons: string[];
}

export interface CoreClaimDto {
  id: string;
  claimNumber: string;
  policyId: string;
  policyNumber: string;
  policyholderName: string;
  registrationNumber?: string;
  make?: string;
  model?: string;
  dateOfLoss: string;
  dateReported: string;
  peril: string | number;
  status: string | number;
  lossDescription: string;
  currentReserveAmount: number;
  assessedLossAmount?: number;
  bettermentDeduction?: number;
  excessDeduction?: number;
  netPayableAmount?: number;
  totalPaidAmount: number;
  dischargeVouchers?: Array<{
    id: string;
    voucherNumber: string;
    voucherAmount: number;
    isSignedByClaimant: boolean;
    authorizedBy?: string;
  }>;
}

export interface CoreIntermediaryDto {
  id: string;
  code: string;
  name: string;
  type: number | string;
  email: string;
  phone: string;
  taxIdentificationNumber?: string;
  regulatoryLicenseNumber?: string;
  creditPeriodDays: number;
  creditLimit: number;
  currentOutstandingBalance: number;
  isUnderwritingSuspended: boolean;
  withholdingTaxRate: number;
  motorCommissionRate: number;
  propertyCommissionRate: number;
  isActive: boolean;
}

export interface BrokerStatementDto {
  summary: {
    intermediaryId: string;
    intermediaryName: string;
    intermediaryCode: string;
    totalDebits: number;
    totalCredits: number;
    closingBalance: number;
    isCreditLimitBreached: boolean;
    aging: {
      current_0_To_30_Days: number;
      pastDue_31_To_60_Days: number;
      pastDue_61_To_90_Days: number;
      over_90_Days: number;
      totalOutstandingBalance: number;
    };
  };
  transactions: Array<{
    id: string;
    transactionDate: string;
    transactionType: string;
    referenceNumber: string;
    description: string;
    debitAmount: number;
    creditAmount: number;
    runningBalance: number;
    isSettled: boolean;
  }>;
}

export interface CoreTreatyDto {
  id: string;
  treatyCode: string;
  treatyName: string;
  treatyType: number | string;
  lineOfBusiness: number | string;
  underwritingYear: number;
  retentionLimit: number;
  maxLines: number;
  maxCapacity: number;
  cedingCommissionRate: number;
  status: number | string;
  participants: Array<{
    id: string;
    reinsurerId: string;
    participationPercentage: number;
    isLeader: boolean;
    reinsurer?: {
      code: string;
      name: string;
      creditRating: string;
    };
  }>;
}

export interface PolicyApportionmentDto {
  policyId: string;
  policyNumber: string;
  totalSumInsured: number;
  grossWrittenPremium: number;
  retainedSumInsured: number;
  retainedGrossPremium: number;
  retainedPercentage: number;
  totalCededSumInsured: number;
  totalCededGrossPremium: number;
  totalCededPercentage: number;
  totalCedingCommissionEarned: number;
  netReinsurancePremiumPayable: number;
  requiresFacultativePlacement: boolean;
  facultativeSumInsuredRequired: number;
  cessions: Array<{
    id: string;
    cessionNumber: string;
    cessionType: number | string;
    cededSumInsured: number;
    cededPercentage: number;
    cededGrossPremium: number;
    cedingCommissionEarned: number;
    netPremiumDueToReinsurer: number;
  }>;
}

export interface RiskBordereauScheduleItemDto {
  itemNumber: number;
  policyNumber: string;
  cessionNumber: string;
  policyholderName: string;
  inceptionDate: string;
  expiryDate: string;
  riskDescription: string;
  totalSumInsured: number;
  grossWrittenPremium: number;
  retainedSumInsured: number;
  retainedGrossPremium: number;
  cededSumInsured: number;
  cededGrossPremium: number;
  cedingCommissionRate: number;
  cedingCommissionEarned: number;
  netReinsurancePremium: number;
  cessionType: number | string;
  treatyCode: string;
  leadingReinsurer: string;
}

export interface ClaimsBordereauScheduleItemDto {
  itemNumber: number;
  claimNumber: string;
  policyNumber: string;
  dateOfLoss: string;
  dateReported: string;
  peril: number | string;
  claimStatus: number | string;
  totalClaimSettlement: number;
  reinsuranceSharePercentage: number;
  recoverableAmount: number;
  recoveredAmount: number;
  outstandingRecovery: number;
  recoveryStatus: number | string;
  recoveryNumber: string;
  leadingReinsurer: string;
}

export interface BordereauFinancialSummaryDto {
  totalRisksCeded: number;
  totalSumInsuredCeded: number;
  totalGrossPremiumCeded: number;
  totalCedingCommissionEarned: number;
  netReinsurancePremiumPayable: number;
  totalClaimsCount: number;
  totalGrossClaimsSettled: number;
  totalReinsuranceClaimsRecoverable: number;
  netBalanceDueToReinsurers: number;
  rbmSolvencyCapitalReliefAmount: number;
  netRetentionRatio: number;
}

export interface RiskBordereauReportDto {
  treatyId: string;
  treatyCode: string;
  treatyName: string;
  lineOfBusiness: number | string;
  underwritingYear: number;
  quarter: number | string;
  quarterDescription: string;
  reportType: number | string;
  generatedAt: string;
  leadingReinsurerName: string;
  financialSummary: BordereauFinancialSummaryDto;
  riskSchedules: RiskBordereauScheduleItemDto[];
  claimsSchedules: ClaimsBordereauScheduleItemDto[];
}

export interface BatchAutoCessionResultDto {
  message: string;
  processedPoliciesCount: number;
  cessionsCreatedCount: number;
  results: PolicyApportionmentDto[];
}

export interface GatewayStatusDto {
  provider: string;
  configured: boolean;
  isSandbox: boolean;
  baseUrl: string;
  supportedChannels: string[];
  webhookUrl: string;
}

export interface GatewayStatusResponseDto {
  payChangu: GatewayStatusDto;
  airtelMoney: GatewayStatusDto;
  tnmMpamba: GatewayStatusDto;
}

export interface GatewayCheckoutRequestDto {
  policyId: string;
  policyNumber: string;
  policyholderName: string;
  policyholderEmail: string;
  policyholderPhone: string;
  amount: number;
  currency?: string;
  provider: number; // 1=PayChangu, 2=AirtelMoney, 3=TnmMpamba
  channel?: number;
  returnUrl?: string;
  callbackUrl?: string;
}

export interface GatewayCheckoutResultDto {
  success: boolean;
  provider: number;
  transactionReference: string;
  externalTransactionId?: string;
  checkoutUrl?: string;
  ussdPromptSent: boolean;
  ussdPromptMessage: string;
  status: number;
  rawGatewayResponse?: string;
}

export interface GatewayWebhookSimulationCommandDto {
  provider: number;
  transactionReference: string;
  externalReference?: string;
  amount: number;
  phoneNumber: string;
  status?: string;
  failureReason?: string;
}

export interface GatewayWebhookSimulationResultDto {
  simulatedPayload: {
    provider: number;
    contentType: string;
    serializedJson: string;
    signatureHeaderName?: string;
    signatureHeaderValue?: string;
  };
  bound: boolean;
  policyNumber?: string;
  digitalCoverNoteNumber?: string;
  verificationToken?: string;
  debitNoteNumber?: string;
  amountPaid?: number;
  coverNotePdfBytes?: number;
  windshieldDiscPdfBytes?: number;
  message: string;
}

export const coreInsuranceApi = {
  // --- Policies ---
  getPolicies: async (): Promise<CorePolicyDto[]> => {
    const res = await apiClient.get<CorePolicyDto[]>('/admin/core/policies');
    return res.data;
  },

  calculateQuote: async (request: QuoteCalculationRequest): Promise<QuoteCalculationResult> => {
    const res = await apiClient.post<QuoteCalculationResult>('/admin/core/policies/quote', request);
    return res.data;
  },

  bindPolicy: async (command: any): Promise<any> => {
    const res = await apiClient.post('/admin/core/policies/bind', command);
    return res.data;
  },

  endorsePolicy: async (policyId: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/policies/${policyId}/endorse`, command);
    return res.data;
  },

  downloadCoverNotePdf: async (policyId: string, coverNoteNumber: string) => {
    const res = await apiClient.get(`/admin/core/policies/${policyId}/cover-note-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `CoverNote_${coverNoteNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  downloadSchedulePdf: async (policyId: string, policyNumber: string) => {
    const res = await apiClient.get(`/admin/core/policies/${policyId}/schedule-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Schedule_${policyNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  downloadDebitNotePdf: async (policyId: string, debitNoteNumber: string) => {
    const res = await apiClient.get(`/admin/core/policies/${policyId}/debit-note-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DebitNote_${debitNoteNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  downloadWindshieldDiscPdf: async (policyId: string, regOrPolicy: string) => {
    const res = await apiClient.get(`/admin/core/policies/${policyId}/windshield-disc-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `WindshieldDisc_${regOrPolicy}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  // --- Road Traffic Directorate & Police Verification ---
  verifyCoverNote: async (tokenOrIdentifier: string, params?: { officerBadge?: string; checkpoint?: string; gps?: string }): Promise<CoverNoteVerificationResultDto> => {
    const res = await apiClient.get<CoverNoteVerificationResultDto>(`/public/verification/verify-cover-note/${encodeURIComponent(tokenOrIdentifier)}`, {
      params,
      skipAuth: true,
    } as any);
    return res.data;
  },

  getRecentVerificationScans: async (): Promise<VerificationAuditSummaryDto[]> => {
    const res = await apiClient.get<VerificationAuditSummaryDto[]>('/public/verification/recent-scans', {
      skipAuth: true,
    } as any);
    return res.data;
  },

  // --- Claims ---
  getClaims: async (): Promise<CoreClaimDto[]> => {
    const res = await apiClient.get<CoreClaimDto[]>('/admin/core/claims');
    return res.data;
  },

  lodgeFnol: async (command: any): Promise<any> => {
    const res = await apiClient.post('/admin/core/claims/fnol', command);
    return res.data;
  },

  submitAssessment: async (claimId: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/claims/${claimId}/assessment`, command);
    return res.data;
  },

  issueDischargeVoucher: async (claimId: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/claims/${claimId}/discharge-voucher`, command);
    return res.data;
  },

  signDischargeVoucher: async (claimId: string, voucherId: string): Promise<any> => {
    const res = await apiClient.post(`/admin/core/claims/${claimId}/discharge-vouchers/${voucherId}/sign`);
    return res.data;
  },

  authorizeSettlement: async (claimId: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/claims/${claimId}/authorize`, command);
    return res.data;
  },

  disbursePayment: async (claimId: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/claims/${claimId}/disburse`, command);
    return res.data;
  },

  downloadDischargeVoucherPdf: async (claimId: string, voucherNumber: string) => {
    const res = await apiClient.get(`/admin/core/claims/${claimId}/discharge-voucher-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DischargeVoucher_${voucherNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  // --- Intermediaries & Bordereaux ---
  getIntermediaries: async (): Promise<CoreIntermediaryDto[]> => {
    const res = await apiClient.get<CoreIntermediaryDto[]>('/admin/core/intermediaries');
    return res.data;
  },

  createIntermediary: async (command: any): Promise<any> => {
    const res = await apiClient.post('/admin/core/intermediaries', command);
    return res.data;
  },

  getIntermediaryStatement: async (id: string): Promise<BrokerStatementDto> => {
    const res = await apiClient.get<BrokerStatementDto>(`/admin/core/intermediaries/${id}/statement`);
    return res.data;
  },

  recordRemittance: async (id: string, command: any): Promise<any> => {
    const res = await apiClient.post(`/admin/core/intermediaries/${id}/remittance`, command);
    return res.data;
  },

  submitBrokerBordereau: async (command: any): Promise<any> => {
    const res = await apiClient.post('/admin/core/intermediaries/bordereaux/submit', command);
    return res.data;
  },

  reconcileBrokerBordereau: async (bordereauId: string, reconciledBy = 'Admin User'): Promise<any> => {
    const res = await apiClient.post(`/admin/core/intermediaries/bordereaux/${bordereauId}/reconcile`, { reconciledBy });
    return res.data;
  },

  // --- Reinsurance ---
  getTreaties: async (): Promise<CoreTreatyDto[]> => {
    const res = await apiClient.get<CoreTreatyDto[]>('/admin/core/reinsurance/treaties');
    return res.data;
  },

  apportionPolicy: async (policyId: string): Promise<PolicyApportionmentDto> => {
    const res = await apiClient.post<PolicyApportionmentDto>(`/admin/core/reinsurance/policies/${policyId}/apportion`);
    return res.data;
  },

  runBatchAutoCessions: async (command?: { lineOfBusiness?: number; policyIds?: string[] }): Promise<BatchAutoCessionResultDto> => {
    const res = await apiClient.post<BatchAutoCessionResultDto>('/admin/core/reinsurance/auto-cessions/run', command || {});
    return res.data;
  },

  getRiskBordereau: async (treatyId: string, year: number, quarter: number): Promise<RiskBordereauReportDto> => {
    const res = await apiClient.get<RiskBordereauReportDto>(
      `/admin/core/reinsurance/bordereau?treatyId=${treatyId}&year=${year}&quarter=${quarter}`
    );
    return res.data;
  },

  downloadBordereauCsv: async (treatyId: string, year: number, quarter: number, scheduleType: 'underwriting' | 'claims' = 'underwriting', treatyCode = 'TREATY') => {
    const res = await apiClient.get(
      `/admin/core/reinsurance/bordereau/export-csv?treatyId=${treatyId}&year=${year}&quarter=${quarter}&scheduleType=${scheduleType}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${scheduleType === 'claims' ? 'Claims' : 'Underwriting'}_Bordereau_${treatyCode}_${year}_Q${quarter}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  downloadBordereauPdf: async (treatyId: string, year: number, quarter: number, treatyCode = 'TREATY') => {
    const res = await apiClient.get(
      `/admin/core/reinsurance/bordereau/export-pdf?treatyId=${treatyId}&year=${year}&quarter=${quarter}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Risk_Bordereau_${treatyCode}_${year}_Q${quarter}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  // --- Payment Gateways & Sandbox Simulation ---
  getPaymentGatewayStatuses: async (): Promise<GatewayStatusResponseDto> => {
    const res = await apiClient.get<GatewayStatusResponseDto>('/admin/payments/gateways/status');
    return res.data;
  },

  testGatewayCheckout: async (request: GatewayCheckoutRequestDto): Promise<GatewayCheckoutResultDto> => {
    const res = await apiClient.post<GatewayCheckoutResultDto>('/admin/payments/gateways/test-checkout', request);
    return res.data;
  },

  testGatewayWebhook: async (command: GatewayWebhookSimulationCommandDto): Promise<GatewayWebhookSimulationResultDto> => {
    const res = await apiClient.post<GatewayWebhookSimulationResultDto>('/admin/payments/gateways/test-webhook', command);
    return res.data;
  },

  verifyGatewayTransaction: async (provider: number, reference: string, amount = 0): Promise<any> => {
    const res = await apiClient.get(`/admin/payments/gateways/verify/${provider}/${reference}?amount=${amount}`);
    return res.data;
  },

  // --- RBM Regulatory & Solvency Engine ---
  getMasterQuarterlyReturn: async (year = 2026, quarter = 2, capital = 2500000000): Promise<MasterRbmQuarterlyReturnDto> => {
    const res = await apiClient.get<MasterRbmQuarterlyReturnDto>(
      `/admin/core/regulatory/rbm/quarterly-return?year=${year}&quarter=${quarter}&availableCapital=${capital}`
    );
    return res.data;
  },

  getUprRunoffSchedule: async (): Promise<any> => {
    const res = await apiClient.get('/admin/core/regulatory/upr-runoff');
    return res.data;
  },

  getSolvencyMargin: async (capital = 2500000000, year = 2026, quarter = 2): Promise<RbmSolvencyMarginDto> => {
    const res = await apiClient.get<RbmSolvencyMarginDto>(
      `/admin/core/regulatory/solvency-margin?availableCapital=${capital}&year=${year}&quarter=${quarter}`
    );
    return res.data;
  },

  // --- Commercial Fleets & COMESA ---
  calculateFleetQuote: async (request: CommercialFleetQuoteRequestDto): Promise<CommercialFleetQuoteResultDto> => {
    const res = await apiClient.post<CommercialFleetQuoteResultDto>('/admin/core/fleet/quote', request);
    return res.data;
  },

  bindFleetPolicy: async (command: { quote: CommercialFleetQuoteResultDto; paymentMethod: string; paymentReference: string; amountPaid: number }): Promise<BoundFleetPolicyDto> => {
    const res = await apiClient.post<BoundFleetPolicyDto>('/admin/core/fleet/bind', command);
    return res.data;
  },

  getBoundFleets: async (): Promise<BoundFleetPolicyDto[]> => {
    const res = await apiClient.get<BoundFleetPolicyDto[]>('/admin/core/fleet/list');
    return res.data;
  },

  downloadFleetSchedulePdf: async (policyNumber: string) => {
    const res = await apiClient.get(`/admin/core/fleet/${policyNumber}/schedule-pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `FleetSchedule_${policyNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  downloadComesaYellowCardPdf: async (cardNo: string) => {
    const res = await apiClient.get(`/admin/core/fleet/yellow-card/${cardNo}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ComesaYellowCard_${cardNo}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  getFleetTelematicsAudit: async (fleetName: string, registrations: string[]): Promise<FleetTelematicsAuditReportDto> => {
    const res = await apiClient.post<FleetTelematicsAuditReportDto>('/admin/core/fleet/telematics/audit', {
      fleetName,
      registrations
    });
    return res.data;
  },

  // --- Garages & Work Orders ---
  getApprovedGarages: async (): Promise<AuthorizedGarageDto[]> => {
    const res = await apiClient.get<AuthorizedGarageDto[]>('/admin/core/garages');
    return res.data;
  },

  getCertifiedAdjusters: async (): Promise<CertifiedLossAdjusterDto[]> => {
    const res = await apiClient.get<CertifiedLossAdjusterDto[]>('/admin/core/garages/adjusters');
    return res.data;
  },

  getAllWorkOrders: async (): Promise<CoreRepairWorkOrderDto[]> => {
    const res = await apiClient.get<CoreRepairWorkOrderDto[]>('/admin/core/garages/work-orders');
    return res.data;
  },

  issueWorkOrder: async (command: any): Promise<CoreRepairWorkOrderDto> => {
    const res = await apiClient.post<CoreRepairWorkOrderDto>('/admin/core/garages/work-orders', command);
    return res.data;
  },

  updateWorkOrderProgress: async (id: string, status: number, note: string): Promise<CoreRepairWorkOrderDto> => {
    const res = await apiClient.patch<CoreRepairWorkOrderDto>(`/admin/core/garages/work-orders/${id}/progress`, { status, note });
    return res.data;
  },

  certifyWorkOrder: async (id: string, certificateNumber: string, remarks: string): Promise<CoreRepairWorkOrderDto> => {
    const res = await apiClient.post<CoreRepairWorkOrderDto>(`/admin/core/garages/work-orders/${id}/certify`, { certificateNumber, remarks });
    return res.data;
  },

  downloadWorkOrderPdf: async (id: string, woNumber: string) => {
    const res = await apiClient.get(`/admin/core/garages/work-orders/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `WorkOrder_${woNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  getClaimEvidence: async (claimId: string): Promise<ClaimInspectionEvidenceDto[]> => {
    const res = await apiClient.get<ClaimInspectionEvidenceDto[]>(`/admin/core/garages/evidence/${claimId}`);
    return res.data;
  },

  addClaimEvidence: async (evidence: any): Promise<ClaimInspectionEvidenceDto> => {
    const res = await apiClient.post<ClaimInspectionEvidenceDto>('/admin/core/garages/evidence', evidence);
    return res.data;
  }
};

export interface RbmRevenueAccountItemDto {
  lineOfBusiness: number | string;
  grossWrittenPremium: number;
  reinsuranceCededPremium: number;
  netWrittenPremium: number;
  unearnedPremiumReserveOpening: number;
  unearnedPremiumReserveClosing: number;
  changeInUpr: number;
  netEarnedPremium: number;
  claimsPaidGross: number;
  reinsuranceClaimsRecoveries: number;
  claimsPaidNet: number;
  outstandingClaimsReserveOpening: number;
  outstandingClaimsReserveClosing: number;
  changeInOslr: number;
  ibnrReserve: number;
  netIncurredClaims: number;
  grossCommissionsPaid: number;
  reinsuranceCedingCommissionEarned: number;
  netCommissionExpense: number;
  managementExpenses: number;
  underwritingResultProfitOrLoss: number;
}

export interface RbmTechnicalProvisionsDto {
  valuationDate: string;
  totalUnearnedPremiumReserveUpr: number;
  totalOutstandingClaimsReserveOslr: number;
  totalIncurredButNotReportedIbnr: number;
  totalTechnicalProvisions: number;
  activePoliciesEvaluatedCount: number;
  openClaimsEvaluatedCount: number;
}

export interface RbmSolvencyMarginDto {
  valuationDate: string;
  year: number;
  quarter: number | string;
  netWrittenPremium12Months: number;
  premiumIndexSolvencyRequirement: number;
  claimsIndexSolvencyRequirement: number;
  statutoryMinimumCapitalRequirement: number;
  requiredSolvencyMargin: number;
  availableAdmissibleCapital: number;
  solvencySurplusOrDeficit: number;
  solvencyRatioPercentage: number;
  isCompliantWithRbmStandards: boolean;
  regulatoryComplianceAssessment: string;
}

export interface MasterRbmQuarterlyReturnDto {
  insurerName: string;
  insurerLicenseNumber: string;
  year: number;
  quarter: number | string;
  valuationDate: string;
  revenueAccountForm1: {
    year: number;
    quarter: number | string;
    reportingDate: string;
    linesOfBusiness: RbmRevenueAccountItemDto[];
    totalAggregate: RbmRevenueAccountItemDto;
  };
  technicalProvisionsForm2: RbmTechnicalProvisionsDto;
  solvencyMarginForm3: RbmSolvencyMarginDto;
  ifrs17InsuranceRevenue: number;
  ifrs17InsuranceServiceExpense: number;
  ifrs17InsuranceServiceResult: number;
}

export interface CoverNoteVerificationResultDto {
  isAuthentic: boolean;
  status: number | string; // 1 = ValidAndActive, 2 = Expired, 3 = SuspendedOrCancelled, 4 = NotFound, 5 = InvalidToken
  statusMessage: string;
  verificationBadgeColor: string; // green, red, amber, gray

  // Security & Verification metadata
  verificationToken: string;
  digitalCoverNoteNumber: string;
  policyNumber: string;
  insurerName: string;
  regulatoryAuthority: string;
  underwritingCompany: string;
  verificationTimestamp: string;

  // Masked Policyholder
  policyholderName: string;
  maskedNationalId: string;
  maskedPhone: string;

  // Vehicle Specifications
  registrationNumber: string;
  make: string;
  model: string;
  yearOfManufacture: number;
  maskedChassisNumber: string;
  maskedEngineNumber: string;
  color: string;
  vehicleUsage: string;

  // Coverage & Validity Period
  coverType: string;
  inceptionDate: string;
  expiryDate: string;
  daysRemaining: number;
  isCurrentlyInForce: boolean;
  currency: string;
  sumInsured: number;

  // Road Traffic Compliance
  roadTrafficActCompliance: string;
  authorizedDrivers: string;
  limitationsToUse: string;
  cryptographicSignatureHash: string;
}

export interface VerificationAuditSummaryDto {
  id: string;
  tokenOrNumber: string;
  registrationNumber: string;
  resultStatus: number | string;
  scannedAt: string;
  officerBadgeNumber?: string;
  checkPoint?: string;
  ipAddress?: string;
}

export interface FleetVehicleScheduleItemDto {
  itemId?: string;
  scheduleIndex?: number;
  registrationNumber: string;
  make: string;
  model: string;
  yearOfManufacture: number;
  chassisNumber: string;
  engineNumber?: string;
  vehicleClass: number; // 1 = LightCommercialPickUp, 2 = HeavyCommercialTruck, 3 = PrimeMoverAndTrailer, 4 = PassengerBusCoach, 5 = FuelTankerHazardousGoods, 6 = SpecialConstructionType
  coverType: number; // 1 = Comprehensive, 2 = ThirdPartyFireAndTheft, 3 = ThirdPartyOnly
  sumInsured: number;
  capacityOrTonnage: string;
  hasTrackingSystem: boolean;
  hasComesaYellowCard: boolean;
  comesaCountries?: number[]; // 1=MW, 2=ZM, 3=MZ, 4=ZW, 5=TZ, 6=KE, 7=UG, 8=RW, 9=CD, 10=BI
  baseRatePercent?: number;
  basePremium?: number;
  fleetDiscountPercent?: number;
  fleetDiscountAmount?: number;
  trackingDiscountAmount?: number;
  comesaYellowCardPremium?: number;
  netUnderwritingPremium?: number;
  statutoryLevies?: number;
  taxVat?: number;
  grossWrittenPremium?: number;
}

export interface ComesaYellowCardCertificateDto {
  certificateNumber: string;
  hostPolicyNumber: string;
  vehicleRegistration: string;
  makeModel: string;
  chassisNumber: string;
  insuredName: string;
  coveredTerritories: number[];
  nationalBureau: string;
  validFrom: string;
  validTo: string;
  liabilityLimitThirdParty: number;
  isActive: boolean;
  securityStamp: string;
}

export interface CommercialFleetQuoteRequestDto {
  companyId?: string;
  fleetName: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  physicalAddress?: string;
  past3YearsLossRatioPercent: number;
  vehicles: FleetVehicleScheduleItemDto[];
}

export interface CommercialFleetQuoteResultDto {
  quoteNumber: string;
  fleetName: string;
  totalVehiclesCount: number;
  discountTier: number | string;
  fleetDiscountPercentage: number;
  totalFleetSumInsured: number;
  totalBasePremium: number;
  totalFleetDiscounts: number;
  totalTrackingDiscounts: number;
  totalComesaYellowCardPremiums: number;
  totalNetUnderwritingPremium: number;
  totalStatutoryLevies: number;
  totalValueAddedTax: number;
  totalGrossWrittenPremium: number;
  vehicleSchedules: FleetVehicleScheduleItemDto[];
  yellowCards: ComesaYellowCardCertificateDto[];
}

export interface BoundFleetPolicyDto {
  fleetPolicyId: string;
  policyNumber: string;
  fleetName: string;
  totalVehicles: number;
  totalSumInsured: number;
  grossWrittenPremium: number;
  inceptionDate: string;
  expiryDate: string;
  status: number | string;
  paymentMethod: string;
  paymentReference: string;
  amountPaid: number;
  vehicleSchedules: FleetVehicleScheduleItemDto[];
  yellowCards: ComesaYellowCardCertificateDto[];
}

export interface AuthorizedGarageDto {
  id: string;
  code: string;
  name: string;
  physicalAddress: string;
  city: string;
  contactPerson: string;
  phone: string;
  email: string;
  ratingScore: number;
  isActive: boolean;
  panelBeaterLicenseNumber: string;
  activeJobsCount: number;
}

export interface CertifiedLossAdjusterDto {
  id: string;
  licenseNumber: string;
  fullName: string;
  firmName: string;
  phone: string;
  email: string;
  specialization: string;
  maxAssessmentLimit: number;
  isActive: boolean;
}

export interface ClaimInspectionEvidenceDto {
  id: string;
  claimId: string;
  imageCategory: string;
  imageUrl: string;
  description: string;
  capturedAt: string;
  capturedByGps?: string;
  severityGrade: number; // 1=Minor, 2=Moderate, 3=Severe, 4=TotalLoss
}

export interface CoreRepairWorkOrderDto {
  id: string;
  workOrderNumber: string;
  claimId: string;
  claimNumber: string;
  policyNumber: string;
  registrationNumber: string;
  vehicleMakeModel: string;
  garageId: string;
  garageName: string;
  assessorId?: string;
  assessorName: string;
  authorizedPartsCost: number;
  authorizedLabourCost: number;
  authorizedPaintMaterialsCost: number;
  totalAuthorizedAmount: number;
  issuedDate: string;
  estimatedCompletionDate: string;
  actualCompletionDate?: string;
  status: number; // 1=Authorized, 2=PartsProcurement, 3=RepairsInProgress, 4=PostRepairInspection, 5=CompletedAndDischarged
  authorizedBy: string;
  authorizedAt: string;
  qualityPassCertificateNumber?: string;
  workOrderRemarks: string;
  progressNotes: string[];
}

export interface VehicleTelematicsScoreSummaryDto {
  vehicleRegistration: string;
  totalTripsTracked: number;
  totalDistanceKm: number;
  harshBrakingEvents: number;
  harshAccelerationEvents: number;
  speedingIncidents: number;
  nightDrivingHours: number;
  driverFatigueWarnings: number;
  overallScore: number;
  riskTier: number; // 1=LowRisk_Exemplary, 2=ModerateRisk_Standard, 3=ElevatedRisk_Monitored, 4=CriticalRisk_HighHazard
  premiumAdjustmentPercent: number;
  safetyRecommendations: string;
}

export interface FleetTelematicsAuditReportDto {
  fleetName: string;
  auditDate: string;
  totalMonitoredVehicles: number;
  averageFleetScore: number;
  aggregateRiskTier: number;
  aggregateFleetAdjustmentPercent: number;
  totalHarshEventsRecorded: number;
  vehicleSummaries: VehicleTelematicsScoreSummaryDto[];
}



